<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import {
    trainModelForCulture,
    predictModelForCulture,
    checkModelExists,
    analyzeCycles,
    monitorDriftForCulture
} from '../functions/functions_predictions';

import { useDataStore } from '../stores/useDataStore';
import { useToast } from 'vue-toastification';

const toast = useToast();
const dataStore = useDataStore();

const { t } = useI18n();

const prediction = ref<Predictions | null>(null);

const isTraining = ref(false);
const isPredicting = ref(false);

const modelExists = ref<boolean | null>(null);
const modelMetrics = ref<ModelMetrics | null>(null);

const cyclesResult = ref<AnalyzeResponse | null>(null);
const isAnalyzingCycles = ref(false);

const driftResult = ref<DriftAnalysis | null>(null);
const isMonitoringDrift = ref(false);

// ── Watch sur culture sélectionnée ──────────────────────────────────────────
watch(
    () => dataStore.selectedCulture,
    async (culture) => {
        // Reset tout au changement de culture
        prediction.value = null;
        cyclesResult.value = null;
        driftResult.value = null;
        modelExists.value = null;
        modelMetrics.value = null;

        if (!culture?.specie?.id || !culture?.pbr?.id) {
            modelExists.value = false;
            return;
        }

        // Check modèle
        try {
            const res = await checkModelExists(culture.specie.id, culture.pbr.id, t);
            modelExists.value = res.exists;
            modelMetrics.value = res.metrics ?? null;
        } catch (err: any) {
            toast.error(t('errorCheckingModel', { message: err.message }));
            modelExists.value = false;
        }

        // Si modèle existe → drift auto
        if (modelExists.value) {
            await checkDrift();
        }
    },
    { immediate: true }
);

// ── Drift auto ───────────────────────────────────────────────────────────────
async function checkDrift() {
    if (!dataStore.selectedCulture?.id || !modelExists.value) return;
    try {
        driftResult.value = await monitorDriftForCulture(dataStore.selectedCulture.id, t);
    } catch (err: any) {
        console.warn('Auto-drift check failed:', err.message);
    }
}

onMounted(() => {
    if (modelExists.value) checkDrift();
});

// ── Actions utilisateur ──────────────────────────────────────────────────────
async function onTrainClick() {
    if (!dataStore.selectedCulture?.id) return;
    isTraining.value = true;

    prediction.value = null;
    cyclesResult.value = null;
    driftResult.value = null;

    try {
        await trainModelForCulture(t);

        // Refresh modèle
        const res = await checkModelExists(
            dataStore.selectedCulture.specie.id,
            dataStore.selectedCulture.pbr.id,
            t
        );
        modelExists.value = res.exists;
        modelMetrics.value = res.metrics ?? null;

        if (modelExists.value) {
            await checkDrift();
        }
    } finally {
        isTraining.value = false;
    }
}

async function onPredictClick() {
    if (!dataStore.selectedCulture?.id) return;
    isPredicting.value = true;

    try {
        const pred = await predictModelForCulture(dataStore.selectedCulture.id, t);
        prediction.value = pred ?? null;

    } catch (err: any) {
        toast.error(t('errorPredicting', { message: err.message }));
        prediction.value = null;
    } finally {
        isPredicting.value = false;
    }
}

async function onAnalyzeCyclesClick() {
    if (!dataStore.selectedCulture?.id) return;
    isAnalyzingCycles.value = true;

    try {
        cyclesResult.value = await analyzeCycles(dataStore.selectedCulture.id, t);
        if (cyclesResult.value?.status === 'analyzed') {
        }
    } catch (err: any) {
        toast.error(t('errorAnalyzingCycles', { message: err.message }));
        cyclesResult.value = null;
    } finally {
        isAnalyzingCycles.value = false;
    }
}

async function onMonitorDriftClick() {
    if (!dataStore.selectedCulture?.id) return;
    isMonitoringDrift.value = true;

    try {
        driftResult.value = await monitorDriftForCulture(dataStore.selectedCulture.id, t);
    } catch (err: any) {
        toast.error(t('errorDriftCheck', { message: err.message }));
        driftResult.value = null;
    } finally {
        isMonitoringDrift.value = false;
    }
}
</script>

<template>
    <div v-if="dataStore.selectedCulture" class="module">
        <h2>{{ $t('absorbancePredictionTitle') }}</h2>

        <div v-if="modelExists === null">
            <p>{{ $t('checkingModel') }}</p>
        </div>

        <div v-else-if="!modelExists">
            <p>
                {{ $t('noModelFound', {
                    pbr: dataStore.selectedCulture.pbr.numero_pbr,
                    specie: dataStore.selectedCulture.specie.specie_code
                }) }}
            </p>
            <div class="button-group">
                <button :disabled="isTraining" @click="onTrainClick">
                    {{ isTraining ? $t('trainingInProgress') : $t('trainModel') }}
                </button>
            </div>
        </div>

        <div v-else> <!-- Modèle existe : metrics + boutons + prédictions -->
            <p>
                {{ $t('modelAvailable', {
                    pbr: dataStore.selectedCulture.pbr.numero_pbr,
                    specie: dataStore.selectedCulture.specie.specie_code
                }) }}
            </p>

            <!-- Infos metrics (si dispo) -->
            <div v-if="modelMetrics" class="metrics-grid">
                <div class="metric-card">
                    <strong>{{ $t('mseLabel') }}</strong> {{ modelMetrics.mse.toFixed(3) }}
                    <br>
                    <small>{{ $t('mseDescription') }}</small>
                </div>
                <div v-if="modelMetrics.r2 != null" class="metric-card">
                    <strong>{{ $t('r2Label') }}</strong> {{ modelMetrics.r2.toFixed(3) }}
                    <br>
                    <small>{{ $t('r2Description') }}</small>
                </div>
                <div class="metric-card">
                    <strong>{{ $t('samplesLabel') }}</strong> {{ modelMetrics.samples_train }} / {{
                        modelMetrics.samples_test }}
                    <br>
                    <small>{{ $t('samplesDescription') }}</small>
                </div>
            </div>
            <div v-else>
                <p>{{ $t('metricsUnavailable') }}</p>
            </div>

            <!-- Boutons -->
            <div class="button-group">
                <button :disabled="isTraining" @click="onTrainClick">
                    {{ isTraining ? $t('retrainingInProgress') : $t('retrainModel') }}
                </button>
                <button :disabled="isPredicting || isTraining" @click="onPredictClick">
                    {{ isPredicting
                        ? $t('predictingInProgress')
                        : (prediction !== null && prediction ? $t('refreshPrediction') : $t('generatePrediction'))
                    }}
                </button>
            </div>

            <!-- Affichage prédiction -->
            <div v-if="prediction" class="prediction-section">
                <h3>{{ $t('predictionsTitle') }}</h3>
                <small>{{ $t('predictionsTitleDescription') }}</small>
                <table class="prediction-table">
                    <thead>
                        <tr>
                            <th>{{ $t('dayHeader') }}</th>
                            <th>{{ $t('absorbanceHeader') }}</th>
                            <th>{{ $t('ratioHeader') }}</th>
                            <th>{{ $t('recentRatioHeader') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ prediction.predicted_day }}</td>
                            <td>{{ prediction.predicted_absorbance.toFixed(3) }}</td>
                            <td :class="{ 'alert-low': prediction.plafond_ratio < 0.8 }">
                                {{ prediction.plafond_ratio.toFixed(3) }}
                            </td>
                            <td :class="{ 'alert-low': prediction.recent_plafond_ratio < 0.9 }">
                                {{ prediction.recent_plafond_ratio.toFixed(3) }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Analyse SHAP de la prédiction-->
                <div v-if="prediction?.shap_explanation?.length" class="shap-analysis-section">
                    <h4>{{ $t('shapPredictionAnalysisTitle') }}</h4>
                    <small>{{ $t('shapPredictionAnalysisTitleDescription') }}</small>
                    <table class="shap-table">
                        <thead>
                            <tr>
                                <th>{{ $t('featureName') }}</th>
                                <th>{{ $t('shapValue') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="feature in prediction.shap_explanation" :key="feature.feature">
                                <td>{{ feature.feature }}</td>
                                <td>{{ feature.shap_value.toFixed(4) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>


            <!-- Section Advanced Analysis -->
            <div class="advanced-analysis-section">
                <h3>{{ $t('advancedAnalysisTitle') }}</h3>
                <div class="button-group">
                    <button :disabled="isAnalyzingCycles" @click="onAnalyzeCyclesClick">
                        {{ isAnalyzingCycles ? $t('analyzingCyclesInProgress') : $t('analyzeCyclesButton') }}
                    </button>
                    <button :disabled="isMonitoringDrift || isTraining" @click="onMonitorDriftClick">
                        {{ isMonitoringDrift ? $t('monitoringDriftInProgress') : $t('monitorDriftButton') }}
                    </button>
                </div>

                <!-- Cycles Analyze Results -->
                <div v-if="cyclesResult" class="analysis-section">
                    <h4>{{ $t('analyzeCyclesTitle') }}</h4>
                    <small>{{ $t('analyzeCyclesTitleDescription') }}</small>
                    <div v-if="cyclesResult.stats" class="stats-grid">
                        <div class="stat-card">
                            <strong>{{ $t('meanDecayRatio') }}</strong> {{
                                cyclesResult.stats.mean_decay_ratio?.toFixed(3) || 'N/A' }}
                            <br>
                            <small>{{ $t('meanDecayRatioDescription') }}</small>
                        </div>
                        <div v-if="cyclesResult.stats.std_decay" class="stat-card">
                            <strong>{{ $t('stdDecay') }}</strong> {{ cyclesResult.stats.std_decay.toFixed(3) }}
                            <br>
                            <small>{{ $t('stdDecayDescription') }}</small>
                        </div>
                        <div v-if="cyclesResult.stats.avg_cycle_duration" class="stat-card">
                            <strong>{{ $t('avgCycleDuration') }}</strong> {{
                                cyclesResult.stats.avg_cycle_duration.toFixed(1) }} {{ $t('days') }}
                            <br>
                            <small>{{ $t('avgCycleDurationDescription') }}</small>    
                    </div>
                    </div>

                    <div v-if="cyclesResult.cycles && cyclesResult.cycles.length > 0" class="cycles-table-section">
                        <h5>{{ $t('cyclesTitle') }}</h5>
                        <table class="cycles-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('cycleId') }}</th>
                                    <th>{{ $t('peakAbs') }}</th>
                                    <th>{{ $t('peakDay') }}</th>
                                    <th>{{ $t('durationDays') }}</th>
                                    <th>{{ $t('decayRatio') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="cycle in cyclesResult.cycles" :key="`${cycle.culture_id}-${cycle.cycle_id}`">
                                    <td>{{ cycle.cycle_id }}</td>
                                    <td>{{ cycle.peak_abs.toFixed(3) }}</td>
                                    <td>{{ cycle.peak_day.toFixed(1) }}</td>
                                    <td>{{ cycle.duration_days }}</td>
                                    <td :class="{ 'alert-low': cycle.decay_ratio < 0.8 }">{{
                                        cycle.decay_ratio.toFixed(3) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Drift Analysis -->
                <div v-if=driftResult class="drift-section analysis-section">
                    <h4>{{ $t('driftMonitorTitle') }}</h4>
                    <small>{{ $t('driftMonitorTitleDescription') }}</small>
                    <div class="drift-status"
                        :class="{ 'alert-low': driftResult.drift, 'alert-ok': !driftResult.drift }">
                        <strong v-if="driftResult.drift">{{ $t('driftDetected') }}</strong>
                        <strong v-else>{{ $t('noDriftDetected') }}</strong>
                        <br>
                        <small v-if="driftResult.drift">{{ $t('driftDetectedDescription') }}</small>
                        <small v-else>{{ $t('noDriftDescription') }}</small>
                        <p v-if="driftResult.p_value">p-value: {{ driftResult.p_value.toFixed(3) }}</p>
                        <p v-if="driftResult.recommend_retrain" class="recommend">{{ $t('recommendRetrain') }}</p>
                        <br>
                        <small v-if="driftResult?.recommend_retrain">{{ $t('recommendRetrainDescription') }}</small>
                    </div>
                </div>

                <!-- Feature Stats -->
                <div v-if="driftResult?.features" class="feature-stats-section analysis-section">
                    <h4>{{ $t('featureStatsTitle') }}</h4>
                    <small>{{ $t('featureStatsTitleDescription') }}</small>
                    <table class="feature-stats-table">
                        <thead>
                            <tr>
                                <th>{{ $t('featureName') }}</th>
                                <th>{{ $t('minValue') }}</th>
                                <th>{{ $t('maxValue') }}</th>
                                <th>{{ $t('meanValue') }}</th>
                                <th>{{ $t('stdValue') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(stats, feature) in driftResult.features" :key="feature">
                                <td>{{ feature }}</td>
                                <td>{{ stats.min.toFixed(3) }}</td>
                                <td>{{ stats.max.toFixed(3) }}</td>
                                <td>{{ stats.mean.toFixed(3) }}</td>
                                <td>{{ stats.std.toFixed(3) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Analyse SHAP du modèle -->
                <div v-if="driftResult?.shap" class="shap-analysis-section analysis-section">
                    <h4>{{ $t('shapModelAnalysisTitle') }}</h4>
                    <small>{{ $t('shapModelAnalysisTitleDescription') }}</small>
                    <table class="shap-table">
                        <thead>
                            <tr>
                                <th>{{ $t('featureName') }}</th>
                                <th>{{ $t('shapValue') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="feature in driftResult.shap.top_features" :key="feature.feature">
                                <td>{{ feature.feature }}</td>
                                <td>{{ feature.shap_value.toFixed(4) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    </div>
</template>

<style scoped>
/* Ton style original est conservé à 100 % — rien n’est cassé */
.metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin: 1rem 0;
}

.metric-card {
    background: #f0f8ff;
    padding: 0.5rem;
    border-radius: 4px;
    border-left: 4px solid #007bff;
}

.prediction-section {
    margin-top: 1rem;
}

.prediction-table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
}

.prediction-table th,
.prediction-table td {
    border: 1px solid #ddd;
    padding: 0.5rem;
    text-align: center;
}

.prediction-table th {
    background-color: #f2f2f2;
}

.alert-low {
    color: red;
    font-weight: bold;
}

.alert {
    color: red;
    font-weight: bold;
    padding: 0.5rem;
    background: #ffebee;
    border-radius: 4px;
    margin-top: 0.5rem;
}

.advanced-analysis-section {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #ddd;
}

.analysis-section {
    margin-top: 1rem;
    padding: 1rem;
    background: #f9f9f9;
    border-radius: 4px;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.5rem;
    margin: 0.5rem 0;
}

.stat-card {
    background: #e3f2fd;
    padding: 0.5rem;
    border-radius: 4px;
    border-left: 3px solid #2196f3;
}

.cycles-table-section {
    margin-top: 1rem;
}

.cycles-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
}

.cycles-table th,
.cycles-table td {
    border: 1px solid #ddd;
    padding: 0.4rem;
    text-align: center;
    font-size: 0.9em;
}

.cycles-table th {
    background-color: #f5f5f5;
}

.drift-section {
    margin-top: 1rem;
    padding: 1rem;
    background: #f9f9f9;
    border-radius: 4px;
}

.drift-status {
    padding: 0.5rem;
    border-radius: 4px;
    text-align: center;
}

.drift-status.alert-low {
    background: #ffebee;
    color: red;
    border: 1px solid #f44336;
}

.drift-status.alert-ok {
    background: #e8f5e8;
    color: green;
    border: 1px solid #4caf50;
}

.recommend {
    font-weight: bold;
    color: #ff9800;
    margin-top: 0.5rem;
}

.feature-stats-section,
.shap-analysis-section {
    margin-top: 1rem;
    padding: 1rem;
    background: #f9f9f9;
    border-radius: 4px;
}

.feature-stats-table,
.shap-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
}

.feature-stats-table th,
.feature-stats-table td,
.shap-table th,
.shap-table td {
    border: 1px solid #ddd;
    padding: 0.4rem;
    text-align: center;
    font-size: 0.9em;
}

.feature-stats-table th,
.shap-table th {
    background-color: #f5f5f5;
}
</style>